import { FloatingPortal } from '@floating-ui/react';
import {
  type Alignment,
  autoPlacement,
  autoUpdate,
  offset,
  useFloating,
} from '@floating-ui/react-dom';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { cn } from 'cn';
import { Button, type ButtonProps } from '../Button';

export type DropdownOptionProps = {
  children?: React.ReactNode;
  className?: string;
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => boolean | undefined;
  active?: boolean;
  disabled?: boolean;
  as?: React.ElementType;
};

export interface DropdownProps {
  button?: ButtonProps;
  options?: DropdownOptionProps[];
  alignment?: Alignment;
  className?: string;
  header?: React.ReactNode;
  footer?: React.ReactNode;
}

function DropdownOption({
  children,
  className,
  active,
  disabled,
  ...rest
}: DropdownOptionProps) {
  return (
    <div
      {...rest}
      className={cn(
        'typ-ui hover:bg-blue-5 active:bg-neutral-5 cursor-pointer select-none px-4 py-1.5 hover:text-blue-100',
        'kunde:text-sm kunde:p-3 kunde:rounded-md',
        {
          'bg-neutral-5': active,
          'text-neutral-5': disabled,
        },
        className,
      )}
    >
      {children}
    </div>
  );
}

function Dropdown({
  button,
  alignment,
  className,
  options,
  header,
  footer,
  ...rest
}: DropdownProps) {
  const { refs, floatingStyles } = useFloating({
    middleware: [
      autoPlacement({
        alignment,
        allowedPlacements: [
          'bottom-end',
          'bottom-start',
          'top-end',
          'top-start',
        ],
      }),
      offset(() => {
        return 8;
      }),
    ],
    whileElementsMounted: autoUpdate,
  });
  const renderMenu = ({ open }: { open: boolean }): React.ReactElement => (
    <div className="relative inline-block">
      <MenuButton
        as={Button}
        icon={open ? 'chevron-up' : 'chevron-down'}
        rightAligned
        ref={(node) => {
          refs.setReference(node);
        }}
        {...button}
        {...rest}
      />
      {open && (
        <FloatingPortal>
          <MenuItems
            as={'div'}
            modal={false}
            static
            className={cn(
              'bg-neutral-0 kunde:rounded-md absolute z-20 w-max rounded py-1 shadow-lg focus:ring-0 kunde:p-2',
              className,
            )}
            ref={refs.setFloating}
            style={floatingStyles}
          >
            {header ? header : null}
            {options?.map(({ as, disabled, onClick, ...rest }, index) => (
              <MenuItem as={as} disabled={disabled} key={index}>
                {({ focus, disabled, close }) => (
                  <DropdownOption
                    {...rest}
                    onClick={(event) => {
                      event.stopPropagation();
                      event.preventDefault();
                      if (typeof onClick === 'function') {
                        const returnValue = onClick(event);
                        if (returnValue) {
                          // Close the menu if return value is truthy
                          close();
                        }
                      }
                      return undefined;
                    }}
                    active={focus}
                    disabled={disabled}
                  />
                )}
              </MenuItem>
            ))}
            {footer ? footer : null}
          </MenuItems>
        </FloatingPortal>
      )}
    </div>
  );

  return <Menu>{renderMenu}</Menu>;
}

export default Dropdown;
